import {
  Card,
  CrudMode,
  DataDisplay,
  formatCurrency,
  formatDate,
  Grid,
  GridBreak,
  pn,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { DatePicker, Input, Select } from "components";
import { StudentScholarship, StudentScholarshipEditResource, StudentScholarshipViewResource } from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { StudentScholarshipPage } from "./StudentScholarshipPage";

export const StudentScholarshipPageBody = (props: {
  crudMode: CrudMode;
  scholarship: StudentScholarship;
  setScholarship: (value: StudentScholarship) => void;
  viewResource: StudentScholarshipViewResource;
  editResource: StudentScholarshipEditResource;
  validationErrors: ValidationErrors;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, fullFieldBreakpoints } = useElementBreakpoints();
  const properties = props.viewResource.properties || [];

  const dateField = () => {
    const propertyName = pn<StudentScholarship>("date");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatDate(props.scholarship.date)}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.scholarship.date}
        onChange={(value) =>
          props.setScholarship(
            produce(props.scholarship, (draft) => {
              draft.date = value;
            })
          )
        }
      />
    );
  };

  const scholarshipTypeField = () => {
    const propertyName = pn<StudentScholarship>("scholarshipTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.scholarship.scholarshipType?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.scholarshipTypes?.find((x) => x.id === props.scholarship.scholarshipTypeId)}
        options={props.editResource.scholarshipTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setScholarship(
            produce(props.scholarship, (draft) => {
              draft.scholarshipTypeId = value?.id;
            })
          )
        }
      />
    );
  };

  const scholarshipNameField = () => {
    const propertyName = pn<StudentScholarship>("scholarshipName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.scholarship.scholarshipName}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.scholarship.scholarshipName}
        onChange={(e) =>
          props.setScholarship(
            produce(props.scholarship, (draft) => {
              draft.scholarshipName = e.target.value;
            })
          )
        }
      />
    );
  };

  const scholarshipPaymentMethodField = () => {
    const propertyName = pn<StudentScholarship>("scholarshipPaymentMethodId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.scholarship.scholarshipPaymentMethod?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.paymentMethods?.find((x) => x.id === props.scholarship.scholarshipPaymentMethodId)}
        options={props.editResource.paymentMethods}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setScholarship(
            produce(props.scholarship, (draft) => {
              draft.scholarshipPaymentMethodId = value?.id;
            })
          )
        }
      />
    );
  };

  const statusField = () => {
    const propertyName = pn<StudentScholarship>("statusId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.scholarship.status?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.statuses?.find((x) => x.id === props.scholarship.statusId)}
        options={props.editResource.statuses}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setScholarship(
            produce(props.scholarship, (draft) => {
              draft.statusId = value?.id;
            })
          )
        }
      />
    );
  };

  const amountField = () => {
    const propertyName = pn<StudentScholarship>("amount");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatCurrency(props.scholarship.amount)}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.scholarship.amount}
        onChange={(e) =>
          props.setScholarship(
            produce(props.scholarship, (draft) => {
              draft.amount = parseFloat(e.target.value);
            })
          )
        }
      />
    );
  };

  const sourceNameField = () => {
    const propertyName = pn<StudentScholarship>("sourceName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.scholarship.sourceName}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.scholarship.sourceName}
        onChange={(e) =>
          props.setScholarship(
            produce(props.scholarship, (draft) => {
              draft.sourceName = e.target.value;
            })
          )
        }
      />
    );
  };

  const websiteField = () => {
    const propertyName = pn<StudentScholarship>("website");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.scholarship.website}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.scholarship.website}
        onChange={(e) =>
          props.setScholarship(
            produce(props.scholarship, (draft) => {
              draft.website = e.target.value;
            })
          )
        }
      />
    );
  };

  const scholarshipNoteField = () => {
    const propertyName = pn<StudentScholarship>("note");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName} type="textarea">
        {props.scholarship.note}
      </DataDisplay>
    ) : (
      <Input
        multiline
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.scholarship.note}
        onChange={(e) =>
          props.setScholarship(
            produce(props.scholarship, (draft) => {
              draft.note = e.target.value;
            })
          )
        }
      />
    );
  };

  return (
    <Grid ref={breakpointsRef} container spacing="sm">
      <Grid item xs={smallFieldBreakpoints}>
        {dateField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {scholarshipTypeField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {amountField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {scholarshipNameField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {scholarshipPaymentMethodField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {statusField()}
      </Grid>



      <Grid item xs={smallFieldBreakpoints}>
        {sourceNameField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {websiteField()}
      </Grid>

      <GridBreak />

      <Grid item xs={fullFieldBreakpoints}>
        {scholarshipNoteField()}
      </Grid>
    </Grid>
  );
};
